import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ResourceIconProvider } from '@spacegame/ui';
import { spriteUrl } from '@spacegame/assets';
import { dwLogoWordmark } from '@spacegame/assets/brand';
import { App } from './App';
import { GameProvider } from './game/GameProvider';
import { AuthGate, ApplyPendingName } from './game/AuthGate';
import { AuthProvider } from './game/auth-context';
import { SettingsProvider } from './game/SettingsProvider';
import { createSupabaseAuth } from './game/supabase-auth';
import { BackendClient } from './game/backend-client';
import { initSentry } from './game/sentry';
import { OAuthRedirectNotice } from './game/OAuthRedirectNotice';

initSentry();

const auth = createSupabaseAuth();
const backendClient = new BackendClient(auth);

/**
 * Last-resort crash screen. Deliberately does not touch `useGame()` — a
 * render crash anywhere in the tree (including inside the provider) must
 * still show this, so it cannot depend on game context being intact.
 */
function CrashFallback() {
  return (
    <div className="sg-root flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center">
      <p className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-content">
        Unerwarteter Fehler
      </p>
      <p className="max-w-sm text-xs leading-relaxed text-content-muted">
        Die Ansicht ist abgestürzt. Der Fehler wurde gemeldet — ein Neuladen stellt deinen
        Spielstand wieder her, da er laufend gespeichert wird.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-control border border-edge-strong bg-surface-2 px-3 py-1.5 font-display text-label uppercase text-content hover:bg-surface-3"
      >
        Neu laden
      </button>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <SettingsProvider>
        <OAuthRedirectNotice />
        <ResourceIconProvider resolve={(resource) => spriteUrl(`res-${resource}`)}>
          <AuthProvider auth={auth}>
            <AuthGate
              auth={auth}
              logo={<img src={dwLogoWordmark} alt="Dyson Warfare" className="h-10 w-auto object-contain" />}
            >
              <GameProvider client={backendClient} auth={auth}>
                <ApplyPendingName />
                <App />
              </GameProvider>
            </AuthGate>
          </AuthProvider>
        </ResourceIconProvider>
      </SettingsProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
